import moment from 'moment'
import { getLocalUser } from 'utils/localStorage'
import i18next from 'i18next'
import { ALL_LICENSED_PATH_STRING, FEATURE_KEYS } from './constants'
import { isAllowedFeature } from 'utils/permissionCheck'

export const compileGoalSteps = (steps, currentGoalStepId) => {
  const l1Steps = steps.filter(s => s.milestone_level === 1)
  const l2Steps = steps.filter(s => s.milestone_level === 2)
  const l3Steps = steps.filter(s => s.milestone_level === 3)
  const getSubSteps = (sourceArray, sourcIndex, targetArray) => {
    const lowerBound = sourceArray[sourcIndex].number
    const noHigherBound = sourcIndex === sourceArray.length - 1 ? true : false
    return targetArray.filter(
      t =>
        t.number > lowerBound &&
        (noHigherBound || t.number < sourceArray[sourcIndex + 1].number)
    )
  }
  const resultSteps = l1Steps.map((l1Step, index) => {
    const childL2Steps = getSubSteps(l1Steps, index, l2Steps)
    if (childL2Steps.length) {
      childL2Steps[childL2Steps.length - 1].isLastL2Step = true
    }
    const processedl1 = l1Step
    processedl1.childL2Steps = childL2Steps.map((l2Step, l2Index) => {
      const processedl2 = l2Step
      const arrayIndex = l2Steps.indexOf(l2Step)
      const childL3Steps = getSubSteps(l2Steps, arrayIndex, l3Steps)
      processedl2.childL3Steps = childL3Steps
      processedl2.l1StepNumber = l1Step.number
      // Calculate total length seconds of L2 step
      processedl2.lengthSeconds = childL3Steps.reduce(
        (acc, curr) => acc + curr?.lengthSeconds ?? 0,
        0
      )
      if (l2Index === 0) {
        processedl2.isFristL2ForL1 = true
        if (index === 0) {
          processedl2.isFirstL2ForGoal = true
        }
      }
      return processedl2
    })
    // Calculate total length seconds of L1 step
    processedl1.lengthSeconds = processedl1.childL2Steps.reduce(
      (acc, curr) => acc + curr.lengthSeconds ?? 0,
      0
    )

    if (childL2Steps.length > 0) {
      childL2Steps[0].encouragementMessage = processedl1.encouragementMessage
    }

    return processedl1
  })

  const goalLengthSeconds = resultSteps.reduce(
    (acc, curr) => acc + curr.lengthSeconds ?? 0,
    0
  )

  // Calculate stats
  const stats = {
    goalLengthSeconds,
    completedSeconds: 0,
    remainingSeconds: 0,
    numberOfCompletedL1Steps: 0,
    currentL1Milestone: null,
    nexL2tMilestone: null
  }

  // Calculate completed by summing up all l2Steps time before currentGoalStepId
  const previousStep = l2Steps.find(s => s.id === currentGoalStepId)

  const currentStepIndex = previousStep ? l2Steps.indexOf(previousStep) + 1 : 1
  const completedSteps = l2Steps.slice(0, currentStepIndex)
  stats.completedSeconds = completedSteps?.reduce(
    (acc, curr) => acc + curr.lengthSeconds ?? 0,
    0
  )
  stats.remainingSeconds = stats.goalLengthSeconds - stats.completedSeconds

  const completedL1Steps = [...new Set(completedSteps.map(s => s.l1StepNumber))]

  stats.numberOfCompletedL1Steps =
    completedL1Steps.length > 0 ? completedL1Steps.length - 1 : 0 // last one is in progress

  stats.nexL2tMilestone = l2Steps[currentStepIndex]
  stats.currentL1Milestone = l1Steps.find(
    s => s.number === stats.nexL2tMilestone?.l1StepNumber
  )

  return {
    l1Steps,
    l2Steps,
    l3Steps,
    resultSteps,
    stats
  }
}

export const parseGoals = (goals, leadershipModels) => {
  const user = getLocalUser()
  const { userStates } = user

  const completedGoals = userStates.completedGoals
  const userLicensedGoalIds = user?.licensedGoalIds
  const licenseAllGoals =
    (!user.isCohortUser && !userLicensedGoalIds) ||
    (userLicensedGoalIds &&
      userLicensedGoalIds.includes(ALL_LICENSED_PATH_STRING))
  return goals.map(g => {
    g.leadershipPathName = `${g.leadershipModelName}: ${g.name}`

    // Set completed, licensed and currentGoal (Booleans) status
    g.licensed =
      isAllowedFeature(FEATURE_KEYS.GOAL_CONTENT) &&
      ((licenseAllGoals &&
        !g.leadershipModel?.userId &&
        !g.leadershipModel?.feature) ||
        userLicensedGoalIds?.includes(g.id))
    g.completed = completedGoals?.indexOf(g.id) > -1
    g.currentGoal =
      userStates.currentGoalId === g.id &&
      (!userStates.completedGoals ||
        userStates.completedGoals.indexOf(userStates.currentGoalId) < 0)
    g.completedCurrentGoal = userStates.currentGoalId === g.id && g.completed

    const leadershipModel = leadershipModels.find(
      lm => lm.id === g.leadershipModelId
    )
    if (leadershipModel || g.licensed) {
      g.display = true
    }
    // Check if goal is Locked
    if (
      g.display &&
      g.available &&
      g.supportLanguages.includes(i18next.resolvedLanguage)
    ) {
      if (user.isCohortUser) {
        // Only active goal in cohort session allowed
        const allowedGoal = user.cohorts?.find(c => {
          const session = c.cohortSessions?.find(s => s.goalId === g.id)
          if (session) {
            g.cohortSession = session
            g.cohortSession.cohortName = c.name
            g.opensAt = null
            g.overDue = false
            if (moment.utc(session.startDate).isAfter(moment.utc())) {
              g.opensAt = session.startDate
            } else if (moment.utc(session.endDate).isBefore(moment.utc()))
              g.overDue = true
            return true
          }
          return false
        })
        if (g.licensed) {
          g.isLocked = false
        } else {
          g.isLocked = !(allowedGoal || g.completed || g.currentGoal)
          if (!g.isLocked) {
            g.licensed = true
          }
        }
      } else {
        g.isLocked = false
      }
    } else {
      g.isLocked = true
    }

    return g
  })
}
